import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CheckCircle2, Crown, Lock, Sparkles, XCircle, Loader2, RefreshCw, ArrowRight, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import { useBilling } from "@/context/BillingContext";
import useSupportEmail from "@/lib/useSupportEmail";

const PLAN_ORDER = ["free", "basic", "pro", "ai_manager"];
const planRank = (id) => PLAN_ORDER.indexOf(id);

const PLAN_LABELS = {
  free: "Free",
  basic: "Basic",
  pro: "Pro",
  ai_manager: "AI Manager™",
};

const ctaLabel = (planId) => {
  if (planId === "free") return "Inizia gratis";
  if (planId === "basic") return "Passa a Basic";
  if (planId === "pro") return "Passa a Pro";
  if (planId === "ai_manager") return "Sblocca AI Manager™";
  return "Scegli";
};

// Post-payment screen states: idle → verifying → success | timeout
const POST_PAYMENT_IDLE = "idle";
const POST_PAYMENT_VERIFYING = "verifying";
const POST_PAYMENT_SUCCESS = "success";
const POST_PAYMENT_TIMEOUT = "timeout";

export default function PlansPage() {
  const { billing, refresh } = useBilling();
  const location = useLocation();
  const navigate = useNavigate();
  const supportEmail = useSupportEmail();
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState("");
  const [cycle, setCycle] = useState("monthly"); // "monthly" | "yearly"

  // Post-payment flow state
  const [postPayment, setPostPayment] = useState(POST_PAYMENT_IDLE);
  const [activatedPlan, setActivatedPlan] = useState(null);
  const [pendingSessionId, setPendingSessionId] = useState(null);

  useEffect(() => {
    api.get("/billing/plans").then((r) => setPlans(r.data?.plans || []));
    // Always pull fresh billing state on Plans page mount: an admin may have
    // changed the user's plan since the last refresh.
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Returning from Stripe → run polling fallback in case the webhook is delayed.
  // The session_id is persisted in sessionStorage so a page refresh during the
  // verification phase doesn't lose it and does not hang the UI.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlSessionId = params.get("session_id");
    const canceled = params.get("canceled");
    if (canceled) {
      toast.info("Pagamento annullato. Puoi riprovare quando vuoi.");
      window.sessionStorage.removeItem("pending_checkout_session");
      navigate("/app/plans", { replace: true });
      return;
    }
    // Fall back to sessionStorage when the URL has been cleaned (e.g. after F5)
    const storedSessionId = window.sessionStorage.getItem("pending_checkout_session") || "";
    const sessionId = urlSessionId || storedSessionId;

    // eslint-disable-next-line no-console
    console.log("[checkout-debug] session_id from URL:", urlSessionId, "from storage:", storedSessionId, "final:", sessionId);

    if (!sessionId) return;

    // Persist so refresh doesn't lose it
    window.sessionStorage.setItem("pending_checkout_session", sessionId);

    setPendingSessionId(sessionId);
    setPostPayment(POST_PAYMENT_VERIFYING);

    // Strip query string only if it's there — don't navigate when we're
    // already on /app/plans without query (avoids an unnecessary replace).
    if (urlSessionId) {
      navigate("/app/plans", { replace: true });
    }

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 10; // 10 × 2s = 20s total

    const tick = async () => {
      if (cancelled) return;
      attempts++;
      try {
        // eslint-disable-next-line no-console
        console.log(`[checkout-debug] tick ${attempts}/${maxAttempts} session=${sessionId}`);
        const { data } = await api.get(`/billing/checkout-status/${sessionId}`);
        // eslint-disable-next-line no-console
        console.log("[checkout-debug] response:", data);
        if (data.payment_status === "paid" || data.activated) {
          if (cancelled) return;
          setActivatedPlan(data.plan || data.metadata?.plan || null);
          setPostPayment(POST_PAYMENT_SUCCESS);
          window.sessionStorage.removeItem("pending_checkout_session");
          await refresh();
          return;
        }
        if (data.status === "expired" || data.status === "canceled") {
          if (cancelled) return;
          toast.error("Sessione scaduta. Riprova.");
          setPostPayment(POST_PAYMENT_IDLE);
          window.sessionStorage.removeItem("pending_checkout_session");
          return;
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[checkout-debug] poll error:", err?.response?.status, err?.response?.data);
        // 404 means the session doesn't belong to this user / has been cleaned
        // — no point to keep polling
        if (err?.response?.status === 404) {
          if (!cancelled) {
            setPostPayment(POST_PAYMENT_TIMEOUT);
            return;
          }
        }
        // otherwise keep polling
      }
      if (attempts < maxAttempts && !cancelled) {
        setTimeout(tick, 2000);
      } else if (!cancelled) {
        setPostPayment(POST_PAYMENT_TIMEOUT);
      }
    };
    tick();

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const retryStatusCheck = async () => {
    if (!pendingSessionId) return;
    setPostPayment(POST_PAYMENT_VERIFYING);
    try {
      // eslint-disable-next-line no-console
      console.log("[checkout-debug] manual retry session=", pendingSessionId);
      const { data } = await api.get(`/billing/checkout-status/${pendingSessionId}`);
      // eslint-disable-next-line no-console
      console.log("[checkout-debug] manual retry response:", data);
      if (data.payment_status === "paid" || data.activated) {
        setActivatedPlan(data.plan || data.metadata?.plan || null);
        setPostPayment(POST_PAYMENT_SUCCESS);
        window.sessionStorage.removeItem("pending_checkout_session");
        await refresh();
      } else {
        setPostPayment(POST_PAYMENT_TIMEOUT);
      }
    } catch {
      setPostPayment(POST_PAYMENT_TIMEOUT);
    }
  };

  const dismissPostPayment = () => {
    setPostPayment(POST_PAYMENT_IDLE);
    setActivatedPlan(null);
    setPendingSessionId(null);
    window.sessionStorage.removeItem("pending_checkout_session");
  };

  const checkout = async (planId) => {
    if (planId === "free") return;
    setBusy(planId);
    try {
      const { data } = await api.post("/billing/create-checkout-session", {
        plan: planId,
        cycle,
        plan_mode: billing?.plan_mode || "both",
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else throw new Error("URL Stripe non disponibile");
    } catch (err) {
      // BILLING HARDENING (Fase 1) — the server now returns 409 with a
      // stable application code inside `detail.code`. Map the code to a
      // user-visible message and, when possible, a useful action. NEVER
      // silently retry — the 409 is the whole point of the guard.
      const detail = err.response?.data?.detail;
      const code = typeof detail === "object" ? detail?.code : null;

      if (code === "manual_entitlement_active") {
        toast.error("Piano assegnato manualmente", {
          description:
            "Il tuo account ha un accesso attivo assegnato manualmente. " +
            "Contatta il supporto per gestire l'abbonamento.",
          duration: 8000,
        });
      } else if (code === "subscription_already_exists") {
        toast.error("Abbonamento già attivo", {
          description:
            "Hai già un abbonamento attivo su questo account. " +
            "Gestiscilo dal portale clienti o contatta il supporto per un cambio piano.",
          duration: 8000,
        });
      } else if (code === "multiple_active_subscriptions") {
        // DO NOT propose retrying — this is a data anomaly that must be
        // resolved manually by an operator before any new checkout.
        toast.error("Abbonamenti multipli rilevati", {
          description:
            "Sono presenti più abbonamenti attivi collegati al tuo account. " +
            "Non è possibile crearne uno nuovo. Contatta il supporto per riconciliare.",
          duration: 12000,
        });
      } else if (code === "checkout_pending") {
        // Reuse the existing pending Session URL if the server sent it.
        if (detail?.url) {
          toast.info("Pagamento già in corso — riapro il checkout aperto");
          window.location.href = detail.url;
          return;
        }
        toast.error("Pagamento già in corso", {
          description:
            "Un checkout è ancora aperto. Completalo oppure attendi qualche minuto prima di riprovare.",
          duration: 8000,
        });
      } else if (code === "stripe_customer_create_failed" || code === "stripe_unavailable") {
        toast.error("Servizio pagamenti momentaneamente non disponibile", {
          description: "Riprova tra qualche istante.",
          duration: 6000,
        });
      } else {
        // Legacy detail is a plain string (fiscal validation errors etc.)
        const msg = typeof detail === "string" ? detail : (detail?.message || err.message);
        toast.error(msg);
      }
      setBusy("");
    }
  };

  const cancelSubscription = async () => {
    const periodEndStr = billing?.current_period_end
      ? new Date(billing.current_period_end).toLocaleDateString("it-IT", {
          day: "numeric", month: "long", year: "numeric",
        })
      : "fine del periodo";
    const ok = window.confirm(
      `Confermi l'annullamento?\n\nManterrai l'accesso completo fino al ${periodEndStr}, poi il tuo account passerà a FREE.\nNessun rimborso per il periodo già pagato.`,
    );
    if (!ok) return;
    try {
      const { data } = await api.post("/billing/cancel");
      toast.success("Abbonamento annullato", {
        description:
          data?.message ||
          `Manterrai l'accesso completo fino al ${periodEndStr}.`,
      });
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
  };

  const resumeSubscription = async () => {
    try {
      await api.post("/billing/resume");
      toast.success("Cancellazione annullata. L'abbonamento si rinnoverà regolarmente.");
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
  };

  const eff = billing?.effective_plan || "free";
  const status = billing?.billing_status || "inactive";
  const cancelScheduled = !!billing?.cancel_at_period_end;
  const sortedPlans = useMemo(
    () => [...plans].sort((a, b) => planRank(a.id) - planRank(b.id)),
    [plans],
  );

  return (
    <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6" data-testid="plans-page">
      {/* Post-payment overlay (success / verifying / timeout) */}
      {postPayment !== POST_PAYMENT_IDLE && (
        <PostPaymentScreen
          state={postPayment}
          plan={activatedPlan}
          onRetry={retryStatusCheck}
          onDismiss={dismissPostPayment}
          onGoDashboard={() => { dismissPostPayment(); navigate("/app/dashboard"); }}
        />
      )}

      {status === "past_due" && (
        <div className="bg-red-50 border border-red-200 rounded-sm p-4 mb-6 flex items-center gap-3" data-testid="past-due-banner">
          <XCircle className="w-4 h-4 text-red-700" />
          <span className="text-sm text-red-900">Abbonamento non attivo (pagamento fallito). Riprova il pagamento per continuare a usare il piano.</span>
        </div>
      )}

      {cancelScheduled && (
        <div
          className="bg-amber-50 border border-amber-200 rounded-sm p-4 mb-6 flex items-start gap-3"
          data-testid="cancel-scheduled-banner"
        >
          <XCircle className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
          <div className="text-sm text-amber-900 flex-1">
            <div className="font-medium">Cancellazione programmata</div>
            <p className="mt-1">
              Manterrai l'accesso completo fino al{" "}
              <span className="font-medium">
                {billing?.current_period_end
                  ? new Date(billing.current_period_end).toLocaleDateString("it-IT", {
                      day: "numeric", month: "long", year: "numeric",
                    })
                  : "termine del periodo"}
              </span>
              . Cambia idea?{" "}
              <button
                type="button"
                onClick={resumeSubscription}
                className="underline font-medium hover:text-amber-950"
                data-testid="resume-subscription"
              >
                Annulla la cancellazione
              </button>
              .
            </p>
          </div>
        </div>
      )}

      <div className="text-center sm:text-left">
        <span className="label-uppercase text-primary">Piani & Abbonamenti</span>
        <h1 className="font-serif-heading text-4xl sm:text-5xl mt-2">Scegli il piano perfetto per te</h1>
        <p className="text-sm text-neutral-600 mt-2">
          Piano attuale: <span className="font-medium uppercase">{eff === "ai_manager" ? "AI Manager™" : eff}</span>
          {billing?.current_period_end && eff !== "free" && (
            <>
              {" · "}
              {cancelScheduled ? "termina il" : "rinnovo il"}{" "}
              {new Date(billing.current_period_end).toLocaleDateString("it-IT")}
            </>
          )}
        </p>
      </div>

      {/* Cycle toggle */}
      <div className="mt-7 flex justify-center" data-testid="cycle-toggle">
        <div className="inline-flex bg-secondary/40 border border-border rounded-sm p-1">
          <button
            type="button"
            onClick={() => setCycle("monthly")}
            className={`px-5 py-2 text-sm rounded-sm transition-all ${
              cycle === "monthly" ? "bg-white shadow-sm font-medium" : "text-neutral-500 hover:text-neutral-800"
            }`}
            data-testid="cycle-monthly"
          >
            Mensile
          </button>
          <button
            type="button"
            onClick={() => setCycle("yearly")}
            className={`px-5 py-2 text-sm rounded-sm transition-all flex items-center gap-2 ${
              cycle === "yearly" ? "bg-white shadow-sm font-medium" : "text-neutral-500 hover:text-neutral-800"
            }`}
            data-testid="cycle-yearly"
          >
            Annuale
            <span className="text-[10px] tracking-wider uppercase bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-sm">
              Risparmia
            </span>
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-4 sm:grid-cols-2 gap-5 mt-8">
        {sortedPlans.map((p) => (
          <PlanCard
            key={p.id}
            plan={p}
            cycle={cycle}
            isCurrent={eff === p.id}
            cancelScheduled={cancelScheduled && eff === p.id}
            busy={busy}
            onCheckout={() => checkout(p.id)}
            onCancel={cancelSubscription}
            onResume={resumeSubscription}
          />
        ))}
      </div>

      <p className="text-xs text-neutral-400 mt-8 text-center">
        IVA esclusa · Pagamenti gestiti da Stripe · Puoi annullare in qualsiasi momento.
        I dati di pagamento non transitano dai nostri server.
      </p>

      {/* Discreet support contact — fine pagina, sotto la nota legale. */}
      <p
        className="text-xs text-neutral-500 mt-3 text-center"
        data-testid="plans-support-note"
      >
        Hai domande sui piani? Contattaci:{" "}
        <a
          href={`mailto:${supportEmail}`}
          className="text-primary hover:underline inline-flex items-center gap-1"
          data-testid="plans-support-email"
        >
          <Mail className="w-3 h-3" strokeWidth={1.5} aria-hidden="true" />
          {supportEmail}
        </a>
      </p>
    </div>
  );
}

function PlanCard({ plan, cycle, isCurrent, cancelScheduled, busy, onCheckout, onCancel, onResume }) {
  const isFree = plan.id === "free";
  const isPro = plan.id === "pro";
  const isAI = plan.id === "ai_manager";
  const monthlyEquiv =
    cycle === "yearly" && plan.monthly_equiv_yearly > 0
      ? plan.monthly_equiv_yearly
      : plan.monthly;

  const accent = isAI
    ? "border-2 border-[#1a1a1a] shadow-lg"
    : isPro
    ? "border-2 border-primary shadow-md"
    : "border-border";

  return (
    <div
      className={`relative bg-white rounded-sm p-6 border transition-all flex flex-col ${accent} ${
        isCurrent ? "ring-2 ring-emerald-300" : ""
      }`}
      data-testid={`plan-card-${plan.id}`}
    >
      {isAI && (
        <span className="absolute -top-3 right-5 bg-[#1a1a1a] text-white text-[10px] tracking-wider uppercase px-2 py-1 rounded-sm flex items-center gap-1">
          <Sparkles className="w-3 h-3" strokeWidth={2} /> Top tier
        </span>
      )}
      {plan.recommended && !isAI && (
        <span className="absolute -top-3 right-5 bg-primary text-white text-[10px] tracking-wider uppercase px-2 py-1 rounded-sm flex items-center gap-1">
          <Crown className="w-3 h-3" strokeWidth={2} /> Consigliato
        </span>
      )}
      {isCurrent && (
        <span className="absolute -top-3 left-5 bg-emerald-600 text-white text-[10px] tracking-wider uppercase px-2 py-1 rounded-sm flex items-center gap-1">
          <CheckCircle2 className="w-3 h-3" strokeWidth={2} /> Attuale
        </span>
      )}

      <div className={`label-uppercase ${isAI ? "text-[#1a1a1a]" : "text-primary"}`}>
        {plan.label}
      </div>

      {/* Price block */}
      <div className="mt-3 flex items-baseline flex-wrap gap-x-1.5">
        <span className="font-serif-heading text-5xl">€{Math.round(monthlyEquiv)}</span>
        {!isFree && (
          <>
            <span className="text-base text-neutral-400 font-sans">/ mese</span>
          </>
        )}
      </div>
      {!isFree && (
        <div className="text-xs text-neutral-500 mt-1">+ IVA</div>
      )}
      {!isFree && cycle === "yearly" && (() => {
        const savings = Math.round((plan.monthly - plan.monthly_equiv_yearly) * 12);
        if (savings <= 0) return null;
        return (
          <div
            className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm px-2 py-1"
            data-testid={`plan-savings-${plan.id}`}
          >
            <span aria-hidden>🔥</span>
            <span>Piano annuale: risparmi €{savings}</span>
          </div>
        );
      })()}
      {isFree && (
        <div className="text-xs text-neutral-500 mt-1">Per sempre, senza carta di credito</div>
      )}

      {/* AI Manager value prop */}
      {isAI && (
        <p className="text-xs text-neutral-600 mt-4 leading-relaxed border-t border-border pt-4">
          AI Manager™ ti permette di analizzare fatturato e performance, capire quanto guadagni
          per evento, monitorare tempo e redditività, ottimizzare team e organizzazione, e
          ricevere suggerimenti automatici per migliorare il tuo business.
        </p>
      )}

      {/* Features */}
      <ul className="text-sm text-neutral-700 mt-5 space-y-2 flex-1">
        {plan.features.map((f, i) => (
          <li key={i} className="flex items-start gap-2">
            <CheckCircle2 className={`w-4 h-4 mt-0.5 shrink-0 ${isAI ? "text-[#1a1a1a]" : "text-emerald-600"}`} strokeWidth={1.5} />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      {/* Limited / blocked */}
      {plan.limited && plan.limited.length > 0 && (
        <ul className="text-xs text-neutral-400 mt-4 space-y-1.5">
          {plan.limited.map((f, i) => (
            <li key={i} className="flex items-start gap-2">
              <Lock className="w-3 h-3 mt-0.5 shrink-0" strokeWidth={1.5} />
              <span>{f}</span>
            </li>
          ))}
        </ul>
      )}

      {/* CTA */}
      <div className="mt-6">
        {isFree ? (
          <Button
            variant="outline"
            className="w-full rounded-sm"
            disabled={isCurrent}
            data-testid={`subscribe-${plan.id}`}
          >
            {isCurrent ? "Piano attivo" : ctaLabel(plan.id)}
          </Button>
        ) : isCurrent ? (
          cancelScheduled ? (
            <Button
              variant="outline"
              className="w-full rounded-sm"
              onClick={onResume}
              data-testid={`resume-${plan.id}`}
            >
              Annulla la cancellazione
            </Button>
          ) : (
            <Button
              variant="outline"
              className="w-full rounded-sm"
              onClick={onCancel}
              data-testid={`cancel-${plan.id}`}
            >
              Annulla abbonamento
            </Button>
          )
        ) : (
          <Button
            className={`w-full rounded-sm ${
              isAI
                ? "bg-[#1a1a1a] hover:bg-[#000] text-white"
                : isPro
                ? "bg-primary hover:bg-primary/90 text-primary-foreground"
                : ""
            }`}
            variant={isAI || isPro ? "default" : "outline"}
            disabled={busy !== ""}
            onClick={onCheckout}
            data-testid={`subscribe-${plan.id}`}
          >
            {busy === plan.id ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Reindirizzo…</>
            ) : isAI ? (
              <><Sparkles className="w-4 h-4 mr-2" strokeWidth={1.5} /> {ctaLabel(plan.id)}</>
            ) : (
              ctaLabel(plan.id)
            )}
          </Button>
        )}
      </div>
    </div>
  );
}

function PostPaymentScreen({ state, plan, onRetry, onDismiss, onGoDashboard }) {
  const planLabel = PLAN_LABELS[plan] || (plan ? plan.toUpperCase() : "");
  const [showManualCheck, setShowManualCheck] = useState(false);

  // After 8s in VERIFYING state, surface a manual "Controlla stato pagamento"
  // button so the user never feels stuck.
  useEffect(() => {
    if (state !== POST_PAYMENT_VERIFYING) {
      setShowManualCheck(false);
      return;
    }
    const t = setTimeout(() => setShowManualCheck(true), 8000);
    return () => clearTimeout(t);
  }, [state]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center px-4"
      data-testid={`post-payment-${state}`}
    >
      <div className="bg-white rounded-sm shadow-2xl max-w-md w-full p-8 text-center relative">
        {state === POST_PAYMENT_VERIFYING && (
          <>
            <div className="w-14 h-14 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
              <Loader2 className="w-6 h-6 text-primary animate-spin" strokeWidth={1.5} />
            </div>
            <h2 className="font-serif-heading text-3xl mt-4">Verifica pagamento</h2>
            <p className="text-sm text-neutral-600 mt-2 leading-relaxed">
              Stiamo confermando la transazione con Stripe. Ci vorranno solo pochi secondi…
            </p>
            {showManualCheck && (
              <div className="mt-6">
                <Button
                  variant="outline"
                  className="rounded-sm"
                  onClick={onRetry}
                  data-testid="post-payment-manual-check"
                >
                  <RefreshCw className="w-4 h-4 mr-1.5" strokeWidth={1.5} />
                  Controlla stato pagamento
                </Button>
                <p className="text-xs text-neutral-400 mt-3">
                  Sembra stia impiegando più del solito. Il pagamento è al sicuro: puoi forzare il controllo ora.
                </p>
              </div>
            )}
          </>
        )}

        {state === POST_PAYMENT_SUCCESS && (
          <>
            <div className="w-14 h-14 mx-auto rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center">
              <CheckCircle2 className="w-7 h-7 text-emerald-600" strokeWidth={1.5} />
            </div>
            <h2 className="font-serif-heading text-3xl mt-4" data-testid="post-payment-title">
              Pagamento completato 🎉
            </h2>
            <p className="text-base text-neutral-700 mt-2">
              Il tuo piano è ora attivo
            </p>
            {planLabel && (
              <div className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-sm bg-primary/5 border border-primary/20">
                <Sparkles className="w-4 h-4 text-primary" strokeWidth={1.5} />
                <span className="text-sm">
                  Benvenuto nel piano <span className="font-medium">{planLabel}</span>
                </span>
              </div>
            )}
            <div className="mt-6 grid grid-cols-2 gap-3">
              <Button
                variant="outline"
                className="rounded-sm"
                onClick={onDismiss}
                data-testid="post-payment-stay"
              >
                Inizia subito
              </Button>
              <Button
                className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground"
                onClick={onGoDashboard}
                data-testid="post-payment-dashboard"
              >
                Vai alla dashboard
                <ArrowRight className="w-4 h-4 ml-1.5" strokeWidth={1.5} />
              </Button>
            </div>
          </>
        )}

        {state === POST_PAYMENT_TIMEOUT && (
          <>
            <div className="w-14 h-14 mx-auto rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center">
              <Loader2 className="w-6 h-6 text-amber-600" strokeWidth={1.5} />
            </div>
            <h2 className="font-serif-heading text-3xl mt-4">Verifica pagamento in corso</h2>
            <p className="text-sm text-neutral-600 mt-2 leading-relaxed">
              Se il pagamento è stato effettuato, il piano verrà attivato automaticamente
              entro pochi secondi.
            </p>
            <div className="mt-6 grid grid-cols-2 gap-3">
              <Button
                variant="outline"
                className="rounded-sm"
                onClick={onDismiss}
                data-testid="post-payment-close"
              >
                Chiudi
              </Button>
              <Button
                className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground"
                onClick={onRetry}
                data-testid="post-payment-retry"
              >
                <RefreshCw className="w-4 h-4 mr-1.5" strokeWidth={1.5} />
                Aggiorna
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

